'use client'
import HotelImage from '@/src/components/common/hotels/hotelImage'
import { ReviewGalleryCategory } from '@/src/types/hotels/details'
import CloseIcon from '@mui/icons-material/Close'
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined'
import { Box, Dialog, IconButton, Typography } from '@mui/material'
import { useState } from 'react'

export interface ImageGalleryProps {
    variant: 'mobile' | 'desktop'
    images: string[]
    alt: string
    /** "{count} Photos" heading for the full-gallery dialog. */
    photosCountLabel: (count: number) => string
    viewPhotosLabel: string
    /** Categorized hotel gallery from roomContent. When present, the dialog groups
     *  images under localized category headers; the strip/grid stays flat. */
    galleryCategories?: ReviewGalleryCategory[]
}

// Hotel photo gallery. Mobile = scroll-snap carousel with a "view photos" badge;
// desktop = hero + 2×2 thumbnail grid. Both open a full-screen gallery dialog.
const ImageGallery: React.FC<ImageGalleryProps> = ({
    variant,
    images,
    alt,
    photosCountLabel,
    viewPhotosLabel,
    galleryCategories = []
}) => {
    const [open, setOpen] = useState(false)
    if (images.length === 0) return null

    const openGallery = () => setOpen(true)
    const hasCategories = galleryCategories.length > 0

    const galleryGrid = (categoryImages: string[], keyPrefix: string) => (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 1,
                p: 1
            }}
        >
            {categoryImages.map((src, index) => (
                <HotelImage
                    key={`${keyPrefix}-${src}-${index}`}
                    src={src}
                    alt={`${alt} ${index + 1}`}
                    wrapperSx={{ borderRadius: 1, height: { xs: 240, md: 320 } }}
                />
            ))}
        </Box>
    )

    const galleryDialog = (
        <Dialog fullScreen open={open} onClose={() => setOpen(false)}>
            <Box sx={{ bgcolor: 'neutral.white', minHeight: '100dvh' }}>
                <Box
                    sx={{
                        position: 'sticky',
                        top: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        bgcolor: 'neutral.white',
                        borderBottom: 1,
                        borderColor: 'borders.default',
                        px: 2,
                        py: 1.5,
                        zIndex: 1
                    }}
                >
                    <IconButton onClick={() => setOpen(false)} aria-label="Close gallery">
                        <CloseIcon />
                    </IconButton>
                    <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'textColors.primary' }}>
                        {photosCountLabel(images.length)}
                    </Typography>
                </Box>

                {hasCategories
                    ? galleryCategories.map(category => (
                          <Box key={category.title}>
                              <Typography
                                  sx={{
                                      fontSize: 15,
                                      fontWeight: 600,
                                      color: 'textColors.primary',
                                      px: 2,
                                      pt: 2
                                  }}
                              >
                                  {category.title}
                              </Typography>
                              {galleryGrid(category.images, category.title)}
                          </Box>
                      ))
                    : galleryGrid(images, 'all')}
            </Box>
        </Dialog>
    )

    if (variant === 'mobile') {
        return (
            <Box sx={{ position: 'relative' }}>
                <Box
                    sx={{
                        display: 'flex',
                        overflowX: 'auto',
                        scrollSnapType: 'x mandatory',
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none'
                    }}
                >
                    {images.map((src, index) => (
                        <HotelImage
                            key={`${src}-${index}`}
                            src={src}
                            alt={`${alt} ${index + 1}`}
                            loading={index === 0 ? 'eager' : 'lazy'}
                            fetchPriority={index === 0 ? 'high' : undefined}
                            onClick={openGallery}
                            wrapperSx={{
                                flex: '0 0 100%',
                                width: '100%',
                                height: 280,
                                scrollSnapAlign: 'start'
                            }}
                        />
                    ))}
                </Box>
                <Box
                    onClick={openGallery}
                    sx={{
                        position: 'absolute',
                        bottom: 16,
                        insetInlineEnd: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        bgcolor: 'rgba(0,0,0,0.55)',
                        color: 'neutral.white',
                        borderRadius: 1,
                        px: 1.25,
                        py: 0.75,
                        cursor: 'pointer'
                    }}
                >
                    <PhotoLibraryOutlinedIcon sx={{ fontSize: 16 }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'neutral.white' }}>
                        {viewPhotosLabel}
                    </Typography>
                </Box>
                {galleryDialog}
            </Box>
        )
    }

    const hero = images[0]
    const gridImages = images.slice(1, 5)
    const remaining = images.length - 5

    return (
        <Box sx={{ display: 'flex', gap: 1, height: 360 }}>
            <HotelImage
                src={hero}
                alt={`${alt} 1`}
                loading="eager"
                fetchPriority="high"
                onClick={openGallery}
                wrapperSx={{ flex: 1, minWidth: 0, height: '100%', borderRadius: 1.5 }}
            />
            {gridImages.length > 0 && (
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gridAutoRows: '1fr',
                        gap: 1
                    }}
                >
                    {gridImages.map((src, index) => {
                        const isLastTile = index === gridImages.length - 1 && remaining > 0
                        return (
                            <Box
                                key={`${src}-${index}`}
                                onClick={openGallery}
                                sx={{ position: 'relative', cursor: 'pointer', minHeight: 0 }}
                            >
                                <HotelImage
                                    src={src}
                                    alt={`${alt} ${index + 2}`}
                                    wrapperSx={{ width: '100%', height: '100%', borderRadius: 1.5 }}
                                />
                                {isLastTile && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            borderRadius: 1.5,
                                            bgcolor: 'rgba(0,0,0,0.5)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'neutral.white' }}>
                                            +{remaining}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )
                    })}
                </Box>
            )}
            {galleryDialog}
        </Box>
    )
}

export { ImageGallery }
export default ImageGallery
